
import React from 'react';
import { Progress } from '@/components/ui/progress';

interface MediaProgressIndicatorProps {
  showProgress: boolean;
  uploadPhase: 'idle' | 'compressing' | 'uploading' | 'extracting-thumbnails' | 'finalizing';
  currentFile?: number;
  totalFiles?: number;
  fileType?: 'image' | 'video';
  progressPercentage?: number;
}

export const MediaProgressIndicator = ({ 
  showProgress, 
  uploadPhase,
  currentFile,
  totalFiles,
  fileType,
  progressPercentage
}: MediaProgressIndicatorProps) => {
  if (!showProgress || uploadPhase === 'idle') {
    return null;
  }

  const getPhaseText = () => {
    switch (uploadPhase) {
      case 'compressing':
        if (progressPercentage !== undefined && progressPercentage < 10 && fileType === 'video') {
          return 'Initializing video encoder...';
        }
        if (currentFile && totalFiles) {
          return fileType === 'video'
            ? `Compressing video ${currentFile} of ${totalFiles}... (this may take a few minutes)`
            : `Compressing image ${currentFile} of ${totalFiles}...`;
        }
        return 'Compressing media...';
      case 'uploading':
        if (currentFile && totalFiles) {
          return `Uploading ${currentFile} of ${totalFiles}...`;
        }
        return 'Uploading media...';
      case 'extracting-thumbnails':
        return 'Generating thumbnails...';
      case 'finalizing':
        return 'Finalizing...';
      default:
        return 'Processing...';
    }
  };

  const showProgressBar = progressPercentage !== undefined && 
    (uploadPhase === 'compressing' || uploadPhase === 'uploading');

  return (
    <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg rounded-lg p-4 z-50 min-w-[320px]">
      <div className="flex items-center space-x-3 mb-2">
        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-500 border-t-brand-primary"></div>
        <div className="flex-1 flex items-center justify-between">
          <span className="text-sm text-gray-700 dark:text-gray-200">{getPhaseText()}</span>
          {showProgressBar && (
            <span className="text-sm font-medium text-brand-primary ml-2">
              {Math.round(progressPercentage)}%
            </span>
          )}
        </div>
      </div>
      {showProgressBar && (
        <Progress value={progressPercentage} className="h-2" />
      )}
    </div>
  );
};
